import { SVGAssets } from '@business/assets/vectors';
import { ContextMenuItem } from '@business/workflows/ComponentUI';
import { showToast } from '@business/workflows/ComponentUI/Toast/Toast';
import {
  getSubscriberId,
  incrementAssetRefreshCounter
} from '@ports/common/sessionStateSlice';
import { getUserData } from '@ports/common/userStateSlice';
import { useAddFavoritesMutation } from '@ports/internal-services/favorites/favoritesSlice';
import { useAddPrivateMutation } from '@ports/internal-services/private/privateSlice';
import { handleDownloads } from '@utils/helpers/lib/photoHelpers';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

const contextMenuIconClassName = 'w-4 h-4 text-gray-500';
export const usePhotoContextMenu = ({
  assetFiltersHandler
}: {
  assetFiltersHandler: (assetId?: string) => void;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userDetails = useSelector(getUserData);
  const subscriberId = useSelector(getSubscriberId);
  const userId = userDetails?.userId || '';
  const [isAddDiaryModalOpen, setIsAddDiaryModalOpen] =
    useState<boolean>(false);
  const [isMovePhotoModalOpen, setIsMovePhotoModalOpen] =
    useState<boolean>(false);
  const [isInfoSidebarOpen, setIsInfoSidebarOpen] = useState<boolean>(false);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const [isDeleteAssetConfirm, setIsDeleteAssetConfirm] =
    useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);

  const location = useLocation();
  const source = new URLSearchParams(location.search).get('source');
  const isSharedWithMeSource = source?.toLowerCase?.() === 'sharedwithme';
  const [AddFavorites] = useAddFavoritesMutation();
  const [AddPrivate] = useAddPrivateMutation();

  const handleToggleFavorite = useCallback(
    async (
      assetId: string,
      currentFavoriteStatus: string | undefined,
      e?: React.MouseEvent
    ) => {
      e?.stopPropagation();
      if (e?.currentTarget) {
        if (e.currentTarget?.classList.contains('hover-star-active')) {
          e.currentTarget.classList.remove('hover-star-active');
        } else {
          e.currentTarget.classList.add('hover-star-active');
        }
      }
      const isFavorite = currentFavoriteStatus === 'Active';
      const newStatus = isFavorite ? 'InActive' : 'Active';

      try {
        await AddFavorites({
          createFavorites: {
            subscriberId,
            userId,
            assetId,
            status: newStatus,
            createdBy: userId
          }
        }).unwrap();
        showToast({
          type: 'success',
          isAlert: true,
          alertProps: {
            type: 'success',
            message: isFavorite
              ? t('photosPage.favoriteRemoved')
              : t('photosPage.favoriteSuccess')
          }
        });
        assetFiltersHandler(assetId);
      } catch (err: any) {
        showToast({
          type: 'error',
          isAlert: true,
          alertProps: {
            type: 'error',
            message: err?.message || t('photosPage.favoriteError')
          }
        });
      }
    },
    [subscriberId, userId, assetFiltersHandler]
  );

  const handleMarkAsPrivate = useCallback(
    async (assetId: string) => {
      AddPrivate({
        createPrivate: {
          id: assetId,
          subscriberId,
          assetId,
          status: 'Active',
          createdBy: userId
        }
      })
        .then((res: any) => {
          showToast({
            type: 'success',
            isAlert: true,
            alertProps: {
              type: 'success',
              message: t(`messageCode.${res?.data?.data?.code}`)
            }
          });
          dispatch(incrementAssetRefreshCounter());
          assetFiltersHandler(assetId);
        })
        .catch((err: any) => {
          showToast({
            type: 'error',
            isAlert: true,
            alertProps: {
              type: 'error',
              message: err?.message || t('photosPage.privateError')
            }
          });
        });
    },
    [subscriberId, userId, t, dispatch, assetFiltersHandler]
  );

  const assetContextMenuItems = (asset: any, accessType?: string) =>
    [
      !isSharedWithMeSource &&
        accessType === 'public' && {
          id: '1',
          label: (
            <ContextMenuItem
              icon={<SVGAssets.Shares className={contextMenuIconClassName} />}
              label={t('photosPage.share')}
            />
          ),
          onClick: () => {
            setIsShareModalOpen(true);
            setSelectedAsset(asset);
          }
        },
      !isSharedWithMeSource && {
        id: '2',
        label: (
          <ContextMenuItem
            icon={<SVGAssets.MoveTos className={contextMenuIconClassName} />}
            label={t('photosPage.moveTo')}
          />
        ),
        onClick: () => {
          setSelectedAsset(asset);
          setIsMovePhotoModalOpen(true);
        }
      },
      !isSharedWithMeSource && {
        id: '3',
        label: (
          <ContextMenuItem
            icon={<SVGAssets.InfoIcon className={contextMenuIconClassName} />}
            label={t('photosPage.info')}
          />
        ),
        onClick: () => {
          setSelectedAsset(asset);
          setIsInfoSidebarOpen(true);
        }
      },
      !isSharedWithMeSource && {
        id: '4',
        label: (
          <ContextMenuItem
            icon={
              <SVGAssets.DownloadIcon className={contextMenuIconClassName} />
            }
            label={t('photosPage.download')}
          />
        ),
        onClick: () => {
          if (asset?.url) {
            handleDownloads(asset.url);
          }
        }
      },
      !isSharedWithMeSource && {
        id: '5',
        label: (
          <ContextMenuItem
            icon={<SVGAssets.StarIcon className={contextMenuIconClassName} />}
            label={t('photosPage.favorite')}
          />
        ),
        onClick: () => {
          handleToggleFavorite(asset.id, asset?.favorite?.status);
        }
      },
      !isSharedWithMeSource && {
        id: '6',
        label: (
          <ContextMenuItem
            icon={<SVGAssets.DiaryIcon className={contextMenuIconClassName} />}
            label={t('photosPage.diary')}
          />
        ),
        onClick: () => {
          setSelectedAsset(asset);
          setIsAddDiaryModalOpen(true);
        }
      },
      !isSharedWithMeSource && {
        id: '7',
        label: (
          <ContextMenuItem
            icon={
              accessType === 'public' ? (
                <SVGAssets.LockClosedIcon />
              ) : (
                <SVGAssets.LockOpen />
              )
            }
            label={
              accessType === 'public'
                ? t('photosPage.markAsPrivate')
                : t('photosPage.markAsPublic')
            }
          />
        ),
        onClick: () => {
          handleMarkAsPrivate(asset.id);
        }
      },
      {
        id: '8',
        label: (
          <ContextMenuItem
            icon={<SVGAssets.Delete className={contextMenuIconClassName} />}
            label={t('photosPage.delete')}
            labelClassName="text-red-600"
          />
        ),
        onClick: () => {
          setSelectedAsset(asset);
          setIsDeleteAssetConfirm(true);
        }
      }
    ].filter(Boolean) as any[];
  return {
    assetContextMenuItems,
    isMovePhotoModalOpen,
    isInfoSidebarOpen,
    selectedAsset,
    isDeleteAssetConfirm,
    isAddDiaryModalOpen,
    setIsAddDiaryModalOpen,
    setIsDeleteAssetConfirm,
    setSelectedAsset,
    setIsMovePhotoModalOpen,
    setIsInfoSidebarOpen,
    handleToggleFavorite,
    isShareModalOpen,
    setIsShareModalOpen
  };
};
